// ============================================================================
// Register — public sign-up page (/register). Anyone can sign up; their account
// then WAITS for HQ approval (Leadership → Approvals) before they see the app.
// We ask for as much as possible, including their local church and pastor.
// The database creates their profile automatically from these details
// (see frame.handle_new_user in supabase/migrations).
// ============================================================================
import { useEffect, useRef, useState } from "react";
import { T } from "../utils";
import { FontLink } from "../components/UI";
import { supabase } from "../supabase";
import { buildSignupMetadata } from "../frame/signup";

// Cloudflare Turnstile ("are you human?" check). Free. If the site key is not set
// yet, the widget is skipped (fine for local testing; production needs the key).
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

function Turnstile({ onToken }) {
  const box = useRef(null);
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const render = () => window.turnstile && box.current && !box.current.hasChildNodes() &&
      window.turnstile.render(box.current, { sitekey: TURNSTILE_SITE_KEY, callback: onToken });
    if (window.turnstile) { render(); return; }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = render;
    document.head.appendChild(script);
  }, [onToken]);
  return <div ref={box} style={{ margin: "8px 0 16px" }} />;
}

// Small labelled text box used for every field below
function Field({ label, required, children }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.grey600, marginBottom: 5 }}>
        {label}{required && <span style={{ color: T.red }}> *</span>}
      </div>
      {children}
    </label>
  );
}

const inputStyle = { width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8, border: `1.5px solid ${T.grey200}`, fontSize: 14, fontFamily: "'DM Sans',sans-serif" };

const INTERESTS = [
  { value: "prayer", label: "Prayer" },
  { value: "marketplace", label: "Marketplace (business & work)" },
  { value: "missions", label: "Missions & outreach" },
];

const thisYear = new Date().getFullYear();
const YEARS = Array.from({ length: 90 }, (_, i) => thisYear - 10 - i);

export default function Register() {
  const params = new URLSearchParams(window.location.search);
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", password: "", confirm: "",
    gender: "", year_of_birth: "", city: "", home_church: "", pastor_name: "", pastor_phone: "",
    profession: "", interests: [], wants_cnom: false, referral_code: params.get("code") || "",
  });
  const [captchaToken, setCaptchaToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleInterest = (value) => setForm({
    ...form,
    interests: form.interests.includes(value) ? form.interests.filter((i) => i !== value) : [...form.interests, value],
  });

  // Check the form, then create the account with all details attached
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const metadata = buildSignupMetadata(form);
    if (!metadata.full_name || !form.email.trim() || !metadata.city || !metadata.home_church || !metadata.pastor_name) {
      setError("Please fill in every field marked *."); return;
    }
    if (!metadata.phone) { setError("Please enter a valid Kenyan phone number, e.g. 0712 345 678."); return; }
    if (form.password.length < 10) { setError("Password must be at least 10 characters."); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    if (TURNSTILE_SITE_KEY && !captchaToken) { setError("Please complete the “I am human” check."); return; }

    setLoading(true);
    const { error: err } = await supabase.auth.signUp({
      email: form.email.trim(),
      password: form.password,
      options: { data: metadata, captchaToken: captchaToken || undefined },
    });
    setLoading(false);
    if (err) setError(err.message); else setDone(true);
  };

  const page = { minHeight: "100vh", background: T.cream, display: "flex", justifyContent: "center", padding: "32px 16px", fontFamily: "'DM Sans',sans-serif" };
  const card = { background: T.white, borderRadius: 16, padding: "32px 28px", width: "100%", maxWidth: 520, boxShadow: "0 12px 40px rgba(0,0,0,0.08)", alignSelf: "flex-start" };

  if (done) return (
    <div style={page}><FontLink />
      <div style={{ ...card, textAlign: "center" }}>
        <img src="/logo192.png" alt="CCN" style={{ width: 72, height: 72, marginBottom: 12 }} />
        <div style={{ fontSize: 22, fontWeight: 700, color: T.navy, fontFamily: "'Playfair Display',serif", marginBottom: 10 }}>Welcome!</div>
        <div style={{ fontSize: 14, color: T.grey600, lineHeight: 1.7 }}>Check your email to confirm, then HQ will review your details.</div>
        <a href="/" style={{ display: "inline-block", marginTop: 20, color: T.navy, fontWeight: 600 }}>Go to sign in →</a>
      </div>
    </div>
  );

  return (
    <div style={page}><FontLink />
      <form onSubmit={handleSubmit} style={card}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <img src="/logo192.png" alt="CCN" style={{ width: 64, height: 64 }} />
          <div style={{ fontSize: 22, fontWeight: 700, color: T.navy, fontFamily: "'Playfair Display',serif" }}>Join Clarion Call Network</div>
          <div style={{ fontSize: 13, color: T.grey500, marginTop: 4 }}>HQ reviews every sign-up before full access.</div>
        </div>

        <Field label="Full name" required><input style={inputStyle} value={form.full_name} onChange={set("full_name")} /></Field>
        <Field label="Phone" required><input style={inputStyle} value={form.phone} onChange={set("phone")} placeholder="0712 345 678" /></Field>
        <Field label="Email" required><input style={inputStyle} type="email" value={form.email} onChange={set("email")} /></Field>
        <Field label="Password (at least 10 characters)" required><input style={inputStyle} type="password" value={form.password} onChange={set("password")} /></Field>
        <Field label="Confirm password" required><input style={inputStyle} type="password" value={form.confirm} onChange={set("confirm")} /></Field>

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Field label="Gender">
              <select style={inputStyle} value={form.gender} onChange={set("gender")}>
                <option value="">Choose…</option><option value="Male">Male</option><option value="Female">Female</option>
              </select>
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Year of birth">
              <select style={inputStyle} value={form.year_of_birth} onChange={set("year_of_birth")}>
                <option value="">Choose…</option>{YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </Field>
          </div>
        </div>

        <Field label="City / town" required><input style={inputStyle} value={form.city} onChange={set("city")} /></Field>
        <Field label="Your local church" required><input style={inputStyle} value={form.home_church} onChange={set("home_church")} /></Field>
        <Field label="Your pastor's name" required><input style={inputStyle} value={form.pastor_name} onChange={set("pastor_name")} /></Field>
        <Field label="Your pastor's phone"><input style={inputStyle} value={form.pastor_phone} onChange={set("pastor_phone")} /></Field>
        <Field label="Profession / work"><input style={inputStyle} value={form.profession} onChange={set("profession")} /></Field>

        <Field label="I am interested in">
          {INTERESTS.map((i) => (
            <label key={i.value} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, marginBottom: 6 }}>
              <input type="checkbox" checked={form.interests.includes(i.value)} onChange={() => toggleInterest(i.value)} /> {i.label}
            </label>
          ))}
        </Field>

        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, marginBottom: 14 }}>
          <input type="checkbox" checked={form.wants_cnom} onChange={set("wants_cnom")} /> I am a member of a CNOM church
        </label>

        <Field label="Referral code (if someone invited you)"><input style={inputStyle} value={form.referral_code} onChange={set("referral_code")} /></Field>

        <Turnstile onToken={setCaptchaToken} />

        {error && <div style={{ color: T.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <button type="submit" disabled={loading}
          style={{ width: "100%", padding: 12, borderRadius: 8, background: T.navy, color: "#fff", border: "none", fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
          {loading ? "Signing up…" : "Sign up"}
        </button>
        <div style={{ textAlign: "center", marginTop: 14, fontSize: 13 }}>
          Already have an account? <a href="/" style={{ color: T.navy, fontWeight: 600 }}>Sign in</a>
        </div>
      </form>
    </div>
  );
}
